/**
 * useEnonceReading — la lecture de l'élève sur l'énoncé (la fiche « Ma lecture »), prête à poser :
 * ce qu'`<ActivityStatement reading>` applique, et les props d'`<EnonceReadingSheet>`.
 *
 * Portage de l'ancien `EnonceReader`, geste par geste :
 *  - la lecture vient du compte (`currentUser.profile.reading`), résolue par le serveur : la lecture
 *    adaptée d'office d'un élève signalé Dys qui n'a rien choisi y arrive déjà. Seul l'élève, hors
 *    mode parent, lit pour lui-même (`toEnonceReading`) ; sinon `null`, la feuille standard ;
 *  - fiche ouverte, l'énoncé suit les gestes sans rien envoyer : le réglage part UNE fois, à la
 *    fermeture (`PUT user/reading-settings`, que le réducteur applique dès l'envoi). Une requête par
 *    geste croisait ses réponses, et la taille revenait en arrière ;
 *  - un réglage fait fiche ouverte part aussi quand l'énoncé s'en va avant qu'elle ne se ferme (un
 *    autre énoncé poussé en live, l'écran quitté), sauf si c'est une bascule de compte qui l'emporte :
 *    la requête partirait sous le jeton de l'autre enfant ;
 *  - les mesures d'avant : `reading.sheet.opened`, `reading.settings.changed`.
 *
 * `statementKey` dit quel énoncé est montré (le support, à défaut l'activité).
 */
import * as amplitude from "@amplitude/analytics-react-native"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector, useStore } from "react-redux"

import { type EnonceReadingSheetProps } from "../../../../../../design-system/templates/EnonceReadingSheet"
import { apiRequest } from "../../../../../../data/redux/actions/api"
import { authService } from "../../../../../../data/services/authService"
import {
    isCustomEnonceReading,
    readingPreset,
    sameEnonceReading,
    toEnonceReading,
    toReadingChoice,
    type ApiReader,
} from "../../../../adapters/student/Activity/enonceReadingAdapter"
import { type UIEnonceReading, type UIStatementReading } from "../../../types"
import { selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"

type ReadingRoot = {
    data?: {
        currentUser?: { object?: ApiReader | null }
        family?: { isParentMode?: boolean }
        session?: { object?: { token?: string | null } | null }
    }
}

/** Ce que l'écran tient pour l'énoncé montré : la fiche, et le réglage en cours. */
type ReadingUi = { forKey: unknown; open: boolean; draft: UIEnonceReading | null }

const fresh = (forKey: unknown): ReadingUi => ({ forKey, open: false, draft: null })

export type EnonceReadingView = {
    statement: UIStatementReading | null
    sheet: Omit<EnonceReadingSheetProps, "testID"> | null
}

export function useEnonceReading(statementKey: unknown): EnonceReadingView {
    const dispatch = useDispatch()
    const store = useStore()
    const user = useSelector((state: ReadingRoot) => selectDisplayedStudent(state))
    const isParentMode = useSelector((state: ReadingRoot) => state.data?.family?.isParentMode === true)
    const token = useSelector((state: ReadingRoot) => state.data?.session?.object?.token ?? null)

    const served = user?.profile?.reading
    const roles = user?.roles
    const saved = useMemo(
        () => toEnonceReading({ roles, profile: { reading: served } }, isParentMode),
        [roles, served, isParentMode]
    )

    // Un autre énoncé, ou un autre compte, repart de zéro dès le rendu qui le montre : pas un rendu
    // avec la fiche d'avant ouverte, ni le réglage d'un enfant montré à l'autre.
    const scope = JSON.stringify([statementKey ?? null, token])
    const [stored, setStored] = useState<ReadingUi>(() => fresh(scope))
    const ui = stored.forKey === scope ? stored : fresh(scope)
    const update = useCallback(
        (patch: Partial<ReadingUi>) =>
            setStored((prev) => ({ ...(prev.forKey === scope ? prev : fresh(scope)), ...patch })),
        [scope]
    )

    const save = useCallback(
        (draft: UIEnonceReading | null, against: UIEnonceReading | null) => {
            if (draft == null || against == null || sameEnonceReading(draft, against)) return
            dispatch(apiRequest(authService.updateReadingSettings, {}, toReadingChoice(draft)) as never)
        },
        [dispatch]
    )

    // Le réglage laissé fiche ouverte, lu au départ de l'énoncé. Tenu après chaque rendu montré : au
    // changement d'énoncé, le nettoyage ci-dessous lit encore celui d'avant.
    const left = useRef<{ draft: UIEnonceReading | null; saved: UIEnonceReading | null; token: string | null }>({
        draft: null,
        saved: null,
        token: null,
    })
    useEffect(() => {
        left.current = { draft: ui.draft, saved, token }
    })
    useEffect(
        () => () => {
            const { draft, saved: against, token: owner } = left.current
            const now = (store.getState() as ReadingRoot).data?.session?.object?.token ?? null
            if (now !== owner) return
            save(draft, against)
        },
        [scope, store, save]
    )

    if (saved == null) return { statement: null, sheet: null }
    const shown = ui.draft ?? saved

    const change = (next: UIEnonceReading) => {
        update({ draft: next })
        amplitude.track("reading.settings.changed", toReadingChoice(next))
    }

    return {
        statement: {
            reading: shown,
            custom: isCustomEnonceReading(shown),
            onOpen: () => {
                update({ open: true })
                amplitude.track("reading.sheet.opened", { adapted: saved.adapted })
            },
        },
        sheet: {
            visible: ui.open,
            reading: shown,
            onToggleAdapted: (adapted) => change(readingPreset(adapted)),
            onChange: change,
            onClose: () => {
                save(ui.draft, saved)
                update({ open: false, draft: null })
            },
        },
    }
}
